import { CLIENT_TYPE, Condition } from '@upstart/harmony/lib/constants';
import { CreditPricing } from '@upstart/harmony/lib/legos/creditPricingType';
import { FinanceType } from '@upstart/harmony/lib/legos/constants';
import { range, cloneDeep, has } from 'lodash';
import { useState, useEffect, useCallback } from 'react';
import DealerPricingService from 'services/DealerPricingService';
import { TIERS_MAX_LENGTH, TIERS_MIN_LENGTH, ORDERED_TIERS } from './constants';
import {
  transformToAprConfig,
  transformToCreditPricing,
} from './creditTiersAdapter';
import {
  LeasePricingOptions,
  PurchasePricingOptions,
  Tier,
  TieredAprConfig,
  TieredAprConfigOptions,
} from './types';
import {
  defaultValidators,
  useAprConfigValidator,
  AprConfigValidationResult,
} from './useAprConfigValidator';

export type AprConfigProps = {
  dealerId: string;
  section: {
    type: CLIENT_TYPE;
    condition: Condition;
    year?: string;
    make?: string;
    model?: string;
    trim?: string;
    vin?: string;
  };
  onePayLeaseEnabled?: boolean;
  isAprConfigModal?: boolean;
  currentTieredAprConfig?: TieredAprConfig | null;
};

export type AprConfigResultProps = {
  tieredAprConfig?: TieredAprConfig;
  loading: boolean;
  validationResult: AprConfigValidationResult;
  onTiersDefinitionsUpdate: (
    financeType: AmendendedFinanceType,
    index: number,
    definition: Partial<Tier>,
  ) => void;
  onTiersDefinitionCopy: (
    origin: AmendendedFinanceType,
    target: AmendendedFinanceType,
  ) => void;
  onTiersDefinitionAdd: (financeType: AmendendedFinanceType) => void;
  onTiersDefinitionRemove: (
    financeType: AmendendedFinanceType,
    removeIndex: number,
  ) => void;
  onTiersPurchasePartialUpdate: (
    value: Partial<PurchasePricingOptions>,
  ) => void;
  onTiersLeasePartialUpdate: (value: Partial<LeasePricingOptions>) => void;
  onTiersOptionsPartialUpdate: (value: Partial<TieredAprConfigOptions>) => void;
  save: () => Promise<void>;
  saveForUsed: (
    onSave: (
      newAprConfig: CreditPricing,
      newTieredAprConfig: TieredAprConfig,
    ) => void,
  ) => void;
  copy: (value: TieredAprConfig) => void;
  revert: () => void;
  loadPricingRules: () => void;
  error: { status: number; error: string } | null;
};

export type AmendendedFinanceType = FinanceType.PURCHASE | FinanceType.LEASE;

/**
 * Custom hook for defining credit tiers config
 */
export function useAprConfig({
  dealerId,
  section,
  onePayLeaseEnabled,
  isAprConfigModal,
  currentTieredAprConfig,
}: AprConfigProps): AprConfigResultProps {
  const [tieredAprConfig, setTieredAprConfig] = useState<TieredAprConfig>();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { validationResult } = useAprConfigValidator({
    validators: defaultValidators,
    aprConfig: tieredAprConfig,
    onePayLeaseEnabled,
    condition: section.condition,
  });

  // load config from backend
  const loadPricingRules = useCallback(async () => {
    if (currentTieredAprConfig) {
      setTieredAprConfig(currentTieredAprConfig);
      return;
    }
    if (!dealerId) return;
    setLoading(true);
    const dealerPricingConfig = await DealerPricingService.get({
      dealerId,
      section,
    });
    // convert to ui type
    const transformed = transformToAprConfig(dealerPricingConfig);
    setTieredAprConfig(transformed);
  }, [dealerId, section, currentTieredAprConfig]);

  useEffect(() => {
    if (!isAprConfigModal) {
      loadPricingRules().finally(() => setLoading(false));
    }
    // eslint-disable-next-line
  }, []);

  const revert = async () => {
    loadPricingRules().finally(() => setLoading(false));
  };

  const save = async () => {
    if (!tieredAprConfig) return Promise.resolve();
    setLoading(true);
    const newDealerConfig = transformToCreditPricing(tieredAprConfig);
    return DealerPricingService.update({
      dealerId,
      section,
      creditPricingConfig: newDealerConfig,
    })
      .catch((e) => {
        setError(e);
      })
      .finally(() => setLoading(false));
  };

  const saveForUsed = (
    onSave: (
      newAprConfig: CreditPricing,
      newTieredAprConfig: TieredAprConfig,
    ) => void,
  ) => {
    if (!tieredAprConfig) {
      return;
    }
    const newDealerConfig = transformToCreditPricing(tieredAprConfig);
    onSave(newDealerConfig, { ...tieredAprConfig });
  };

  const copy = (value: TieredAprConfig) => setTieredAprConfig(cloneDeep(value));

  // Tier Definitions Event Handler
  //  1. If tiers are copied from purchase to lease
  //  2. If a tier is removed
  //  3. If a tier is added
  const onTiersDefinitionsUpdate = (
    financeType: AmendendedFinanceType,
    index: number,
    definition: Partial<Tier>,
  ) => {
    if (!tieredAprConfig) return;
    const updatedAprConfig = { ...tieredAprConfig };
    const tiers = updatedAprConfig.tiers?.[financeType];

    // if we're updating isDefault value, set all tiers' isDefault value to false first
    // since we can only have one isDefault per tier set.
    if (has(definition, 'isDefault')) {
      tiers?.forEach((tier) => {
        tier.isDefault = false;
      });
    }

    updatedAprConfig.tiers[financeType] = [
      ...(tiers?.slice(0, index) || []),
      {
        low: '',
        high: '',
        ...tiers?.[index],
        ...definition,
      },
      ...(tiers?.slice(index + 1) || []),
    ];
    setTieredAprConfig(updatedAprConfig);
  };

  const onTiersDefinitionAdd = (financeType: AmendendedFinanceType) => {
    if (!tieredAprConfig) return;

    const updatedAprConfig = { ...tieredAprConfig };
    const clone = [...(updatedAprConfig.tiers?.[financeType] || [])];
    if (clone.length === TIERS_MAX_LENGTH) return;

    const previous = clone?.[clone.length - 1];
    const high = previous?.low
      ? (parseInt(previous.low, 10) - 1)?.toString()
      : '';
    updatedAprConfig.tiers[financeType] = [
      ...clone,
      { low: '', high: high ?? '' },
    ];

    // depending on finance type, we must validate integrity of config if a tier is added
    if (financeType === FinanceType.PURCHASE) {
      const purchaseConfig = updatedAprConfig.pricing[FinanceType.PURCHASE];
      const purchaseMultiplier = purchaseConfig.terms.length;
      const add = range(purchaseMultiplier).map(() => ({
        apr: '',
        markup: '',
      }));

      purchaseConfig.pricing.push(add);
      const lastTierIdx = purchaseConfig.pricing.length - 1;
      updatedAprConfig.options.useManufacturerApr = {
        ...updatedAprConfig.options.useManufacturerApr,
        [ORDERED_TIERS[lastTierIdx]]: false,
      };
    } else if (financeType === FinanceType.LEASE) {
      const leaseConfig = updatedAprConfig.pricing[FinanceType.LEASE];
      const leaseMultiplier = leaseConfig.terms.reduce(
        (accumulator, current) => accumulator + current.mileages.length,
        0,
      );
      const add = range(leaseMultiplier).map(() => ({
        moneyFactor: '',
        onePayLeaseMoneyFactor: '',
      }));
      leaseConfig.pricing.push(add);
    }
    setTieredAprConfig(updatedAprConfig);
  };

  const onTiersDefinitionRemove = (
    financeType: AmendendedFinanceType,
    removeIndex: number,
  ) => {
    if (!tieredAprConfig) return;
    const updatedAprConfig = { ...tieredAprConfig };
    const clone = [...(updatedAprConfig?.tiers?.[financeType] ?? [])];
    if (clone.length <= TIERS_MIN_LENGTH) return;
    updatedAprConfig.tiers[financeType] = [
      ...clone.slice(0, removeIndex),
      ...clone.slice(removeIndex + 1),
    ];
    if (financeType === FinanceType.PURCHASE) {
      updatedAprConfig.pricing[FinanceType.PURCHASE].pricing = [
        ...updatedAprConfig.pricing[FinanceType.PURCHASE].pricing.slice(
          0,
          removeIndex,
        ),
        ...updatedAprConfig.pricing[FinanceType.PURCHASE].pricing.slice(
          removeIndex + 1,
        ),
      ];

      // remove any references to options.useManufacturerApr.tier[n]
      delete updatedAprConfig.options.useManufacturerApr?.[
        ORDERED_TIERS[removeIndex]
      ];
    } else if (financeType === FinanceType.LEASE) {
      updatedAprConfig.pricing[FinanceType.LEASE].pricing = [
        ...updatedAprConfig.pricing[FinanceType.LEASE].pricing.slice(
          0,
          removeIndex,
        ),
        ...updatedAprConfig.pricing[FinanceType.LEASE].pricing.slice(
          removeIndex + 1,
        ),
      ];
    }
    setTieredAprConfig(updatedAprConfig);
  };

  const onTiersDefinitionCopy = (
    origin: AmendendedFinanceType,
    target: AmendendedFinanceType,
  ) => {
    if (origin === target || !tieredAprConfig) return;

    const updatedAprConfig = { ...tieredAprConfig };
    let from = updatedAprConfig?.tiers?.[origin];
    let to = updatedAprConfig?.tiers?.[target];
    if (from && to) {
      if (from.length > to.length) {
        // source has more than target, keep adding until tier lengths are equal
        while (from && to && from.length > to.length) {
          onTiersDefinitionAdd(target);
          from = updatedAprConfig?.tiers?.[origin];
          to = updatedAprConfig?.tiers?.[target];
        }
      } else if (from.length < to.length) {
        // target has more than source, keep removing until tier lengths are equal
        while (from && to && from.length < to.length) {
          onTiersDefinitionRemove(target, to.length - 1);
          from = updatedAprConfig?.tiers?.[origin];
          to = updatedAprConfig?.tiers?.[target];
        }
      }
      updatedAprConfig.tiers[target] = [
        ...(updatedAprConfig.tiers[origin] || []),
      ];
      setTieredAprConfig(updatedAprConfig);
    }
  };

  /**
   * Rebuilds purchase pricing matrix after a partial update
   * @param value
   */
  const onTiersPurchasePartialUpdate = (
    value: Partial<PurchasePricingOptions>,
  ) => {
    if (!tieredAprConfig) return;
    const updatedAprConfig = {
      ...tieredAprConfig,
      pricing: {
        ...tieredAprConfig.pricing,
        purchase: { ...tieredAprConfig.pricing.purchase, ...value },
      },
    };
    setTieredAprConfig(updatedAprConfig);
  };

  /**
   * Rebuilds lease pricing matrix after a partial update
   */
  const onTiersLeasePartialUpdate = (value: Partial<LeasePricingOptions>) => {
    if (!tieredAprConfig) return;
    const updatedAprConfig = {
      ...tieredAprConfig,
      pricing: {
        ...tieredAprConfig.pricing,
        lease: { ...tieredAprConfig.pricing.lease, ...value },
      },
    };
    setTieredAprConfig(updatedAprConfig);
  };

  const onTiersOptionsPartialUpdate = (
    value: Partial<TieredAprConfigOptions>,
  ) => {
    if (!tieredAprConfig) return;

    const updatedAprConfig = {
      ...tieredAprConfig,
      options: {
        ...tieredAprConfig.options,
        ...value,
      },
    };

    setTieredAprConfig(updatedAprConfig);
  };

  return {
    tieredAprConfig,
    validationResult,
    onTiersDefinitionsUpdate,
    onTiersDefinitionAdd,
    onTiersDefinitionRemove,
    onTiersDefinitionCopy,
    onTiersPurchasePartialUpdate,
    onTiersLeasePartialUpdate,
    onTiersOptionsPartialUpdate,
    loading,
    revert,
    save,
    saveForUsed,
    copy,
    loadPricingRules,
    error,
  };
}
